/**
 * The building screen's reading logic, as pure functions.
 *
 * The screen itself is a renderer. Everything it *decides* — what the unit
 * cross-check chip says, what a machine room slot reads, how many slots are
 * still expected, whether the per-car ATS toggle exists at all — is decided
 * here, in plain TypeScript that the node test environment can exercise
 * directly (the same split `surveyList.ts` and `results.ts` already use).
 *
 * ── Where every number comes from ────────────────────────────────────────────
 * Nothing in this file re-derives an engine result:
 *
 *   the cross-check sentence  ← ValidationIssue.message (§4 rule 1), verbatim
 *   the cross-check numbers   ← `unitCountCrossCheck()` in src/core/calc.ts,
 *                               which exists precisely so this screen and rule 1
 *                               read the same arithmetic
 *   section progress          ← `SurveyResults.progressBySection`, which the gap
 *                               engine decides
 *   the ATS toggle's gate     ← `visibleQuestions()`, i.e. `isVisible(B14)`, not
 *                               a local `if (b13 === true)`
 *
 * The only sentences composed here are the ones no rule produces: the reassuring
 * "everything is covered" state (the engine is silent when a rule passes) and
 * the room-count note (B12 has no §4 rule at all). Both are labelled below.
 */

import { unitCountCrossCheck } from '../../core/calc';
import { answerKey, isSubmittedStatus, sectionInstanceId } from '../../core/types';
import type {
  MachineRoom,
  RoomType,
  SurveyDocument,
} from '../../core/types';
import { issuesFor, keyFor, visibleQuestions, type SurveyResults } from './results';

/* ────────────────────────────── small readers ───────────────────────────── */

export const ROOM_TYPE_LABEL: Record<RoomType, string> = {
  traction: 'Traction',
  hydro: 'Hydro',
  mrl: 'MRL',
};

function numberAnswer(doc: SurveyDocument, questionId: string): number | undefined {
  const value = doc.answers[keyFor(questionId)];
  return value?.kind === 'number' ? value.value : undefined;
}

/** B12 — how many machine rooms the surveyor says the building has. */
export function expectedRoomCount(doc: SurveyDocument): number | undefined {
  const declared = numberAnswer(doc, 'B12');
  if (declared === undefined || !Number.isFinite(declared) || declared < 0) return undefined;
  return Math.floor(declared);
}

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/* ─────────────────────────── the unit cross-check ───────────────────────── */

/** A subset of `MathTone`; the screen hands it straight to `<MathChip>`. */
export type CrossCheckTone = 'ok' | 'bad' | 'info';

export interface CrossCheckView {
  tone: CrossCheckTone;
  message: string;
  why?: string;
}

/**
 * Has the surveyor been asked for everything §4 rule 1 compares? — **D-014**.
 *
 * Rule 1 sets B11 (typed on this screen, first thing) against the car lists of
 * every machine room (built later, one screen per room). Until every declared
 * room exists AND has had its cars entered, the two sides are not both collected
 * and a disagreement between them is not a finding — it is the job not being
 * finished yet.
 *
 * "Every declared room exists" uses B12 when it is answered; a survey with no
 * B12 at all settles as soon as the rooms that DO exist all have cars, because
 * nothing has told the form to expect more.
 */
export function crossCheckSettled(doc: SurveyDocument): boolean {
  if (doc.rooms.length === 0) return false;
  const declared = expectedRoomCount(doc);
  if (declared !== undefined && doc.rooms.length < declared) return false;
  return doc.rooms.every((room) => room.cars.length > 0);
}

/**
 * Specification §4 rule 1 — and it speaks exactly once (**D-029 R4**,
 * amending D-014's live chip).
 *
 * The walkthrough's verdict on the running commentary — "3 elevators counted
 * so far … keep going" — was that it is noise, and the green confirmation
 * is commentary too: a rule that passes says nothing. So the chip renders only
 * when the count is SETTLED (every declared room set up, every room carrying
 * cars) and STILL WRONG — and then it is the validation engine's sentence,
 * VERBATIM, because re-wording it here would let the chip and the submitted
 * gap disagree about the same document.
 *
 * Nothing is removed from the engine: V1 still fires, still becomes a gap, and
 * the close screen still shows it at submit. Only the chip's tongue is held.
 */
export function crossCheckView(results: SurveyResults): CrossCheckView | undefined {
  const check = unitCountCrossCheck(results.doc);

  // No total to check against, or no car anywhere to check. Say nothing.
  if (check.buildingTotal === undefined || check.roomTotal === 0) return undefined;

  const failing = issuesFor(results, 'B11').find((issue) => issue.ruleId === 'V1');
  if (!failing || !crossCheckSettled(results.doc)) return undefined;

  return {
    tone: 'bad',
    message: failing.message,
    ...(failing.explanation ? { why: failing.explanation } : {}),
  };
}

/* ─────────────────── B12 is the single control for room slots ───────────── */

export interface RoomDiscard {
  roomId: string;
  name: string;
  cars: number;
  /** Stored answers scoped inside this room. */
  answers: number;
  notes: number;
  photos: number;
}

export interface RoomCountPlan {
  /** The B12 value being asked for, floored at zero. */
  count: number;
  /** Machine rooms that would stop existing, last-declared first to go. */
  discards: RoomDiscard[];
  /** True when at least one of them holds something the surveyor entered. */
  destructive: boolean;
  /** The confirmation's words. Meaningful only when `destructive`. */
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
}

/** Everything keyed inside one machine room shares this answer/section prefix. */
function roomPrefix(roomId: string): string {
  return `room:${roomId}|`;
}

function holdingsOf(doc: SurveyDocument, room: MachineRoom): RoomDiscard {
  const prefix = roomPrefix(room.id);
  return {
    roomId: room.id,
    name: room.name,
    cars: room.cars.length,
    answers: Object.keys(doc.answers).filter((key) => key.startsWith(prefix)).length,
    notes: doc.notes.filter((note) => note.sectionInstanceId.startsWith(prefix)).length,
    photos: doc.photos.filter((photo) => photo.sectionInstanceId.startsWith(prefix)).length,
  };
}

function heldItems(discard: RoomDiscard): number {
  return discard.cars + discard.answers + discard.notes + discard.photos;
}

function listOf(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] ?? ''}`;
}

/**
 * What lowering B12 would cost, computed BEFORE anything is touched — the same
 * shape, and the same promise, as `typeChangePlan` in roomSetup.ts.
 *
 * Room slots derive from B12, so B12 is the only control that adds or removes
 * one (feedback b: "Adding a new machine room isn't necessary; users should
 * simply adjust the count"). That makes lowering it destructive, which is
 * exactly the moment a warning belongs — a real consequence of a real action the
 * surveyor just took, not a complaint about work they have not done yet.
 *
 * A room that holds nothing but its own name and type is dropped silently: there
 * is nothing to lose, and asking about it would be the alarm-for-nothing this
 * whole change exists to remove.
 */
export function roomCountPlan(doc: SurveyDocument, count: number): RoomCountPlan {
  const next = Math.max(0, Math.floor(count));
  const discards = doc.rooms.slice(next).map((room) => holdingsOf(doc, room));
  const destructive = discards.some((discard) => heldItems(discard) > 0);

  const names = listOf(discards.map((discard) => discard.name));
  const cars = discards.reduce((total, discard) => total + discard.cars, 0);
  const answers = discards.reduce((total, discard) => total + discard.answers, 0);
  const notes = discards.reduce((total, discard) => total + discard.notes, 0);
  const photos = discards.reduce((total, discard) => total + discard.photos, 0);

  const held: string[] = [];
  if (cars > 0) held.push(plural(cars, 'car'));
  if (answers > 0) held.push(plural(answers, 'answer'));
  if (notes > 0) held.push(plural(notes, 'note'));
  if (photos > 0) held.push(plural(photos, 'photo'));

  return {
    count: next,
    discards,
    destructive,
    title:
      discards.length === 1
        ? `Discard ${names}?`
        : `Discard ${plural(discards.length, 'machine room survey')}?`,
    body:
      `Setting the building to ${plural(next, 'machine room')} removes ${names} from this ` +
      `survey, and ${listOf(held)} recorded in ${discards.length === 1 ? 'it' : 'them'} ` +
      'would go too. Nothing else in the survey changes.',
    confirmLabel:
      discards.length === 1 ? `Discard ${names}` : `Discard ${String(discards.length)} rooms`,
    cancelLabel: `Keep ${plural(doc.rooms.length, 'machine room')}`,
  };
}

/*
 * A B12 the guard refused, held past the Building screen — D-056.
 *
 * The guard refuses a lower count that would cost a room with work in it, and
 * asks first (`roomCountPlan`). The ask is the screen's own state, so when the
 * refusal comes from the stepper's unmount — the surveyor typed a lower count
 * and left Building with the keyboard up, no blur (iOS, D-053) — the screen
 * that would ask is already gone, and the count was dropped without a word
 * (D-055 §6). Held here instead, per survey and in memory only (a reload is a
 * fresh start, like the screen state it stands in for), and the Building
 * screen asks about it the next time it is on screen. Asked once: taking it
 * clears it.
 */
const refusedRoomCounts = new Map<string, number>();

/** Remember a refused B12 for this survey; the latest refusal wins. */
export function holdRefusedRoomCount(surveyId: string, count: number): void {
  refusedRoomCounts.set(surveyId, count);
}

/**
 * The refused B12 to ask about now, or `null`. Cleared either way. Dropped if
 * it no longer costs a room with work in it — B12 or the rooms changed since,
 * and asking would be an alarm about nothing — and dropped on a submitted
 * survey, which is the office's record: nothing on it may be discarded, so
 * there is nothing to ask.
 */
export function takeRefusedRoomCount(surveyId: string, doc: SurveyDocument): number | null {
  const count = refusedRoomCounts.get(surveyId);
  refusedRoomCounts.delete(surveyId);
  if (count === undefined || isSubmittedStatus(doc.status)) return null;
  return roomCountPlan(doc, count).destructive ? count : null;
}

/** Forget a held count: the screen's own ask was answered. */
export function forgetRefusedRoomCount(surveyId: string): void {
  refusedRoomCounts.delete(surveyId);
}

/**
 * Set B12 and make the room list match it, in ONE document mutation.
 *
 * Both halves have to land in the same transaction. A B12 write followed by a
 * separate prune leaves a window in which the building says two rooms and three
 * exist, and a force-quit inside that window persists it — the same reasoning
 * `applyTypeChange` is built on.
 *
 * Raising the count only writes B12: the extra rooms are pending slots, and a
 * room is still created by the M1 tap that chooses its type.
 */
export function applyRoomCount(doc: SurveyDocument, count: number): void {
  const next = Math.max(0, Math.floor(count));
  doc.answers[answerKey('B12')] = { kind: 'number', value: next };
  if (doc.rooms.length <= next) return;

  const dropped = doc.rooms.slice(next);
  doc.rooms = doc.rooms.slice(0, next);
  for (const room of dropped) {
    const prefix = roomPrefix(room.id);
    for (const key of Object.keys(doc.answers)) {
      if (key.startsWith(prefix)) delete doc.answers[key];
    }
    doc.notes = doc.notes.filter((note) => !note.sectionInstanceId.startsWith(prefix));
    doc.photos = doc.photos.filter((photo) => !photo.sectionInstanceId.startsWith(prefix));
    doc.gaps = doc.gaps.filter((gap) => !gap.sectionInstanceId.startsWith(prefix));
  }
}

/* ─────────── declared rooms that do not exist yet: the slots (D-037) ───── */

/*
 * The slot VIEWS that lived here until 2026-08-31 (RoomSlot, roomSlots,
 * pendingRoomSlots, roomCountNote, carRangeLabel, roomProgress) were a list on
 * the Building screen; D-029 R7 removed the list. The slots came back on
 * 2026-09-02 as ROWS ON THE MAP — *"tapping the number of rooms should
 * generate the corresponding navigation automatically"* — and what is here now
 * is only what the map and the walk share: which rooms are still owed, and the
 * id each will be created under.
 */

export interface RoomSlot {
  /** The id the room is created under when its type is chosen on Room Setup. */
  roomId: string;
  /**
   * "Machine Room 3" — the name the room takes if it is created from THIS
   * slot (final review, F2): the lowest default-pattern number no existing
   * room's name already holds and no earlier slot in the same list has
   * claimed, so two rooms — and two pending slots — never carry the same
   * name. Tapping a hollow row hands its slot's `roomId` to Room Setup, which
   * looks its `name` back up here rather than recomputing `defaultRoomName`
   * (which only ever counts `doc.rooms.length`, blind to which numbers are
   * already in use) — see `RoomSetupScreen.tsx`'s `createRoom`.
   */
  name: string;
  /** 1-based, among the building's rooms, existing rooms first. */
  position: number;
}

/**
 * Deterministic room ids, so the map's pending row and the walk's Start Survey
 * button open the SAME Room Setup: `mr-slot-N` for the lowest N no room on the
 * document holds. A room made from a slot and later dropped by lowering B12
 * leaves nothing behind — the document is cleared by `applyRoomCount` and the
 * device's own walked trail by `forgetWalkedUnder` (`walked.ts`, final review)
 * — so the id is safe to hand out again; an id a room still holds is skipped,
 * never collided with.
 */
export function freeRoomIds(doc: SurveyDocument, count: number): string[] {
  const taken = new Set(doc.rooms.map((room) => room.id));
  const ids: string[] = [];
  for (let n = 1; ids.length < count; n++) {
    const id = `mr-slot-${String(n)}`;
    if (!taken.has(id)) ids.push(id);
  }
  return ids;
}

/** The Room Setup that Start Survey opens: the first free slot. */
export function nextRoomId(doc: SurveyDocument): string {
  return freeRoomIds(doc, 1)[0] ?? 'mr-slot-1';
}

/**
 * B12's rooms beyond the ones that exist, in the order they will be started.
 *
 * Naming a slot `Machine Room ${doc.rooms.length + index + 1}` collided with
 * a real room's name whenever B12 had been lowered past a room and raised
 * again — the freed number stays free for an ID (`freeRoomIds`) but the OLD
 * highest-numbered room is still sitting on the document under a lower one
 * (final review, F2). So a slot's name is the lowest default-pattern number
 * (`Machine Room N`, case-insensitive) neither an existing room nor an
 * earlier slot in this same list already holds — never the room COUNT.
 * `position` is unaffected: it stays the 1-based row index, existing rooms
 * first, because it names where the slot sits, not what it will be called.
 */
export function pendingRoomSlots(doc: SurveyDocument): RoomSlot[] {
  const declared = expectedRoomCount(doc) ?? 0;
  const pending = Math.max(0, declared - doc.rooms.length);

  const held = new Set<number>();
  for (const room of doc.rooms) {
    const match = /^machine room (\d+)$/i.exec(room.name.trim());
    if (match?.[1]) held.add(Number(match[1]));
  }

  const names: string[] = [];
  let candidate = 1;
  for (let i = 0; i < pending; i++) {
    while (held.has(candidate)) candidate++;
    held.add(candidate);
    names.push(`Machine Room ${String(candidate)}`);
    candidate++;
  }

  return freeRoomIds(doc, pending).map((roomId, index) => ({
    roomId,
    name: names[index] ?? `Machine Room ${String(doc.rooms.length + index + 1)}`,
    position: doc.rooms.length + index + 1,
  }));
}

/* ───────────────────────────────── the ATS gate ─────────────────────────── */

/**
 * Whether cars can be tagged "on ATS" — DECISIONS D-003.
 *
 * B13 = Yes is the gate, and it is read through the VISIBILITY ENGINE (B14's own
 * `showWhen`) rather than by testing the B13 answer here. One gate, one
 * evaluator: if the catalog ever changes what opens the ATS chain, the per-car
 * toggle in room setup follows it without anyone remembering to.
 */
export function atsTaggingEnabled(results: SurveyResults): boolean {
  const building = results.sectionById.get(sectionInstanceId('building'));
  if (!building) return false;
  return visibleQuestions(results.doc, building).some((question) => question.id === 'B14');
}
